<script setup lang="ts">
import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic'
import { generateImageSrcSet } from '~/helpers/product-image'
import SwiperCore from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Thumbs, A11y } from 'swiper/modules'

type ImagesSwiperProps =
  | {
      images: ShopperProductsTypes.Image[]
      video?: string
      preOptimisedImages?: never
      isQuickView?: boolean
      sku?: string
      badge?: string
      constructorItemParam: RequiredItemParam
    }
  | {
      images?: never
      video?: string
      preOptimisedImages: {
        src: string
        srcset: string
        sizes: string
        alt: string | undefined
      }[]
      isQuickView?: boolean
      sku?: string
      badge?: string
      constructorItemParam: RequiredItemParam
    }

const props = defineProps<ImagesSwiperProps>()
const emit = defineEmits<{ (e: 'imageClick', index: number): void }>()
// Server-rendered (pre-optimised) images start visible so the LCP image can paint before hydration.
const loadedImages = ref<Map<number, boolean>>(new Map(props.preOptimisedImages?.map((_, index) => [index, true])))
const swiperUtils = ref<SwiperCore | null>(null)
const thumbsSwiper = ref<SwiperCore | null>(null)
const optimisedImageSrcSet = computed(() => {
  if (!props.preOptimisedImages) {
    // controls loading state if sourceset needs to be generated
    loadedImages.value = new Map()
  }
  return props.preOptimisedImages || generateImageSrcSet(props.images, productImageSizes.drawer)
})
const modules = [Thumbs, A11y]

const handleImageLoad = (index: number) => {
  loadedImages.value.set(index, true)
}

const isImageLoaded = (index: number) => {
  return loadedImages.value.get(index) || false
}

const onSwiper = (swiper: SwiperCore) => {
  swiperUtils.value = swiper
}

const setThumbsSwiper = (swiper: SwiperCore) => {
  thumbsSwiper.value = swiper
}

const onSlideChange = (swiper: SwiperCore) => {
  const direction = swiper?.swipeDirection || (swiper.previousIndex < swiper.activeIndex ? 'next' : 'prev')
  pushEventToDataLayer('custom.carousel.navigate', {
    group: 'product images',
    type: swiper?.swipeDirection ? 'swiper' : 'thumb',
    direction: direction,
    info: {
      title: props.images?.[swiper.activeIndex]?.title,
      position: `${swiper.activeIndex}`,
      count: swiper.slides.length,
    },
  })
}

const handleImageClick = (index: number) => {
  if (!props.isQuickView) {
    emit('imageClick', index)
  }
}

onMounted(() => {
  if (useNuxtApp().isHydrating) {
    loadedImages.value = new Map(optimisedImageSrcSet.value.map((_, index) => [index, true]))
  }
})
</script>

<template>
  <div class="images-swiper">
    <Swiper
      @swiper="onSwiper"
      @slideChange="onSlideChange"
      :modules="modules"
      :thumbs="{ swiper: thumbsSwiper }"
      :space-between="4"
      class="images-swiper__main"
    >
      <SwiperSlide v-for="(image, index) in optimisedImageSrcSet" :key="index" class="images-swiper__main-slide">
        <img
          v-show="isImageLoaded(index)"
          @load="handleImageLoad(index)"
          @click="handleImageClick(index)"
          v-bind="image"
          loading="eager"
          :fetchpriority="index === 0 ? 'high' : 'low'"
          class="images-swiper__main-image"
        />
        <div v-show="!isImageLoaded(index)" class="skeleton-image" />
      </SwiperSlide>
      <SwiperSlide v-if="video" class="images-swiper__main-slide">
        <video
          class="images-swiper__main-video"
          :src="video"
          autoplay
          muted
          loop
          playsinline
          @click="handleImageClick(optimisedImageSrcSet.length)"
        />
      </SwiperSlide>
      <span v-if="!isQuickView" class="images-swiper__zoom-indicator" aria-hidden="true">
        <svg-icon name="mhj:click-to-zoom" />
      </span>
      <span v-if="props.badge" class="images-swiper__badge">{{ props.badge }}</span>
      <WishlistAction v-if="sku" :id="sku" :constructorItemParam class="images-swiper__wishlist" />
      <VirtualTryOn v-if="sku && !isQuickView" :sku="sku" :is-mobile="true" />
    </Swiper>
    <Swiper
      :modules="modules"
      :slides-per-view="4.5"
      :space-between="4"
      watch-slides-progress
      @swiper="setThumbsSwiper"
      class="images-swiper__thumb"
    >
      <SwiperSlide v-for="(image, index) in optimisedImageSrcSet" :key="index" class="images-swiper__thumb-slide">
        <img v-show="isImageLoaded(index)" v-bind="image" class="images-swiper__thumb-image" />
        <div v-show="!isImageLoaded(index)" class="skeleton-image skeleton-image--thumb" />
      </SwiperSlide>
      <SwiperSlide v-if="video" class="images-swiper__thumb-slide">
        <video class="images-swiper__thumb-video" :src="video" autoplay muted loop playsinline />
      </SwiperSlide>
    </Swiper>
  </div>
</template>

<style lang="postcss">
.images-swiper {
  .images-swiper__main,
  .images-swiper__thumb {
    position: relative;
    /* Images show before they load rather than behind a skeleton, so they hold the skeleton's box themselves. */
    .images-swiper__main-image,
    .images-swiper__thumb-image {
      width: 100%;
      height: auto;
      aspect-ratio: var(--aspect-ratio-product);
      object-fit: cover;
    }
    .images-swiper__thumb-video,
    .images-swiper__main-video {
      width: 100%;
      height: auto;
      object-fit: cover;
      aspect-ratio: var(--aspect-ratio-product);
    }
  }

  .swiper-slide-thumb-active {
    .images-swiper__thumb-image {
      border-bottom: 2px solid var(--border-dark-10);
    }
    .images-swiper__thumb-video {
      box-sizing: content-box;
      border-bottom: 2px solid var(--border-dark-10);
    }
  }
  .images-swiper__badge {
    position: absolute;
    top: var(--space-50);
    left: var(--space-50);
    pointer-events: none;
    background-color: var(--surface-light-00);
    color: var(--text-primary);
    padding: var(--space-10) var(--space-30);
    height: fit-content;
    text-transform: uppercase;
    font-size: var(--text-xxsmall);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    z-index: 2;
    line-height: normal;
    @media (--desktop) {
      font-size: var(--text-xsmall);
    }
  }
  .images-swiper__zoom-indicator {
    position: absolute;
    left: var(--space-50);
    bottom: var(--space-50);
    z-index: 2;
    pointer-events: none;

    svg.icons {
      width: var(--space-60);
      height: var(--space-60);
      path {
        fill: var(--ui-neutral-30);
      }
    }
  }
  .images-swiper__wishlist {
    position: absolute;
    top: var(--space-50);
    right: var(--space-50);
    z-index: 2;
  }
  .virtual-try-on {
    position: absolute;
    bottom: var(--space-50);
    right: var(--space-50);
    z-index: 1;
  }

  /* Swiper's own size for 4.5 thumbs 4px apart, so server-rendered thumbs don't resize when it starts. */
  .images-swiper__thumb-slide {
    width: calc((100% - 3.5 * 4px) / 4.5);
    margin-right: 4px;
  }

  .skeleton-image--thumb {
    width: 6rem;
  }
}
</style>
